import { useMemo, useState } from 'react';
import { ChatBubbleBottomCenterTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useConversationQuickReplies } from '../../../hooks/useConversationQuickReplies';
import type {
    ConversationQuickReply,
    QuickReplyListFilter
} from '../../../services/conversationQuickReplies';
import { ModalShell } from '../../shared/ModalShell';
import { DeleteQuickReplyConfirmModal } from './DeleteQuickReplyConfirmModal';
import { QuickRepliesManageList } from './QuickRepliesManageList';
import { QuickReplyFormView } from './QuickReplyFormView';
import { useQuickReplyActions } from './useQuickReplyActions';

const BOTH_THREADS: QuickReplyListFilter = {};
const TITLE_ID = 'quick-replies-manage-title';

// reply null creates a new one.
type OpenForm = { reply: ConversationQuickReply | null } | null;

interface QuickRepliesManageModalProps {
    onClose: () => void;
}

export function QuickRepliesManageModal({ onClose }: QuickRepliesManageModalProps) {
    const replies = useConversationQuickReplies(BOTH_THREADS);
    const actions = useQuickReplyActions();
    const [query, setQuery] = useState('');
    const [form, setForm] = useState<OpenForm>(null);
    const [toDelete, setToDelete] = useState<ConversationQuickReply | null>(null);
    const closeForm = () => setForm(null);
    const back = form ? closeForm : onClose;

    const confirmDelete = async (reply: ConversationQuickReply) => {
        await actions.remove(reply);
        setToDelete(null);
    };

    const categories = useMemo(
        () => [...new Set(replies.data.map((reply) => reply.category))].sort(),
        [replies.data]
    );

    return (
        <ModalShell
            widthClassName="max-w-[640px]"
            panelClassName="rounded-xl h-[min(640px,85vh)] flex flex-col overflow-hidden"
            dismissible={!actions.isPending && !toDelete}
            onDismiss={back}
            labelledBy={TITLE_ID}
        >
            <ManageModalHeader form={form} disabled={actions.isPending} onClose={back} />
            {form ? (
                <QuickReplyFormView
                    reply={form.reply}
                    categories={categories}
                    isPending={actions.isPending}
                    onSave={async (body) => {
                        await actions.save(form.reply, body);
                        closeForm();
                    }}
                    onCancel={closeForm}
                />
            ) : (
                <QuickRepliesManageList
                    replies={replies.data}
                    isLoading={replies.isLoading}
                    isError={replies.isError}
                    onRetry={() => void replies.refetch()}
                    query={query}
                    onQueryChange={setQuery}
                    onNew={() => setForm({ reply: null })}
                    onEdit={(reply) => setForm({ reply })}
                    onDelete={setToDelete}
                />
            )}
            {toDelete && (
                <DeleteQuickReplyConfirmModal
                    reply={toDelete}
                    isDeleting={actions.isPending}
                    onConfirm={() => void confirmDelete(toDelete)}
                    onCancel={() => setToDelete(null)}
                />
            )}
        </ModalShell>
    );
}

function headerTitle(form: OpenForm): string {
    if (!form) return 'Quick replies';
    return form.reply ? 'Edit quick reply' : 'New quick reply';
}

interface ManageModalHeaderProps {
    form: OpenForm;
    disabled: boolean;
    onClose: () => void;
}

function ManageModalHeader({ form, disabled, onClose }: ManageModalHeaderProps) {
    const closeLabel = form ? 'Back to quick replies' : 'Close';
    return (
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
            <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-gray-600" />
            </span>
            <div className="flex-1 min-w-0">
                <h2 id={TITLE_ID} className="text-lg font-bold text-gray-800">
                    {headerTitle(form)}
                </h2>
            </div>
            <button
                type="button"
                onClick={onClose}
                disabled={disabled}
                aria-label={closeLabel}
                title={closeLabel}
                className="text-gray-400 hover:text-gray-800 disabled:opacity-50"
            >
                <XMarkIcon className="h-5 w-5" />
            </button>
        </div>
    );
}
